import { Injectable } from '@angular/core';
import { DocumentSection } from 'src/app/models/document-section';
import Fuse from 'fuse.js';
import { MixpanelService } from '../mixpanel/mixpanel.service';

export class SearchResult {
  section: DocumentSection;
  field: string;
  snippet?: string;
  /** The section name with the search term marked, ready to bind as HTML. */
  nameHtml?: string;
}

/** How much of a section to show when there is no matching sentence to quote. */
const TEASER_LENGTH = 200;

/** Longest sentence we will quote verbatim before windowing around the match instead. */
const SNIPPET_LENGTH = 240;

/** Characters of context kept either side of the match when windowing. */
const SNIPPET_CONTEXT = 100;

/**
 * Elements that imply a line break. Profile content is largely bulleted lists, and
 * textContent runs adjacent elements together ("...the partiesIn case of..."), so
 * these boundaries need explicit whitespace before the text is flattened.
 */
const BLOCK_ELEMENTS =
  'address,article,aside,blockquote,br,dd,div,dl,dt,figcaption,figure,footer,h1,h2,h3,h4,h5,h6,' +
  'header,hr,li,main,nav,ol,p,pre,section,table,tbody,td,tfoot,th,thead,tr,ul';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private escapeRegExp(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text ?? '';
    return div.innerHTML;
  }

  private stripHtml(html: string): string {
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    doc.body.querySelectorAll(BLOCK_ELEMENTS).forEach((el) => {
      el.insertAdjacentText('beforebegin', ' ');
      el.insertAdjacentText('afterend', ' ');
    });
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  }

  // Both sides are escaped before matching so a term containing HTML or regex
  // characters neither breaks the expression nor injects markup.
  private highlight(term: string, text: string): string {
    const escapedText = this.escapeHtml(text);
    if (!term) {
      return escapedText;
    }
    const regex = new RegExp(this.escapeRegExp(this.escapeHtml(term)), 'gi');
    return escapedText.replace(regex, (match) => '<mark>' + match + '</mark>');
  }

  // Used when the term matched the section title, so there is no matching
  // sentence to quote: lead with the start of the section instead, so the
  // result reads like the ones matched on their content.
  private extractTeaser(text: string): string {
    const plain = this.stripHtml(text);
    if (plain.length <= TEASER_LENGTH) {
      return plain;
    }
    const truncated = plain.slice(0, TEASER_LENGTH);
    const lastSpace = truncated.lastIndexOf(' ');
    return (lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated) + '…';
  }

  // Keeps SNIPPET_CONTEXT characters either side of the match, trimmed to word
  // boundaries and marked with ellipses. Used when sentence detection yields
  // something too long to read, which happens on list-shaped sections where no
  // item ends in punctuation and the whole list reads as one sentence.
  private windowAroundMatch(term: string, text: string): string {
    const index = text.toLowerCase().indexOf(term.toLowerCase());
    if (index < 0) {
      return '';
    }

    let start = Math.max(0, index - SNIPPET_CONTEXT);
    let end = Math.min(text.length, index + term.length + SNIPPET_CONTEXT);

    if (start > 0) {
      const space = text.indexOf(' ', start);
      if (space > -1 && space < index) {
        start = space + 1;
      }
    }
    if (end < text.length) {
      const space = text.lastIndexOf(' ', end);
      if (space > index + term.length) {
        end = space;
      }
    }

    const excerpt = this.highlight(term, text.slice(start, end).trim());
    return (start > 0 ? '…' : '') + excerpt + (end < text.length ? '…' : '');
  }

  // We need to extract the snippet ourselves, rather than relying on the search index, since its indices
  // are based on the content that includes the full HTML
  private extractSnippet(term: string, text: string): string {
    // Strip the HTML
    const searchText = this.stripHtml(text);

    // Regular expression to find sentences
    const regex = new RegExp(
      '([^.!?]*' + this.escapeRegExp(term) + '[^.!?]*[^.!?\\s][.!?][\'"]?)(\\s|$)',
      'gi'
    );
    const matches = regex.exec(searchText);
    const sentence = matches ? matches[0].trim() : '';
    if (sentence && sentence.length <= SNIPPET_LENGTH) {
      return this.highlight(term, sentence);
    }
    return this.windowAroundMatch(term, searchText);
  }

  searchCountryProfileIndex(
    searchTerm: string,
    sections: DocumentSection[]
  ): SearchResult[] {
    const options = {
      includeScore: true,
      includeMatches: true,
      ignoreLocation: true,
      threshold: 0,
      keys: [
        { name: 'name', weight: 2 },
        'content',
        { name: 'background', weight: 0.5 },
        { name: 'references', weight: 0.5 },
      ],
    };

    const fuse = new Fuse(sections, options);
    const fuseResult: any[] = fuse.search(searchTerm);
    this.mixpanelService.track('Search Country Profile', {
      'Search Term': searchTerm,
    });

    const result = fuseResult.map((x) => {
      const field = x.matches[0]?.key;
      let snippet = '';
      if (field && field !== 'name') {
        snippet = this.extractSnippet(searchTerm, x.item[field]);
      }
      // A title match has no sentence to quote, and a content match can still
      // fail to yield one (no sentence punctuation); either way fall back to the
      // start of the section so every result carries teaser text.
      if (!snippet) {
        const body = x.item.content || x.item.background || x.item.references;
        snippet = this.highlight(searchTerm, this.extractTeaser(body));
      }
      return {
        section: x.item,
        field,
        snippet,
        nameHtml: this.highlight(searchTerm, x.item.name),
      };
    });
    return result;
  }

  constructor(private mixpanelService: MixpanelService) {}
}
